import React, { useState, useEffect } from 'react';
import { Key, Save, RefreshCcw, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Loader2, Mail, CreditCard, BarChart3, Shield, Zap, Image } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';

const serviceIcons = {
  resend: Mail,
  smtp: Mail,
  chatgpt: Zap,
  stripe: CreditCard,
  google_analytics: BarChart3,
  google_ads: BarChart3,
  recaptcha: Shield,
  leonardo: Image,
};

const AdminApiKeys = () => {
  const [configurations, setConfigurations] = useState([]);
  const [editing, setEditing] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/api-keys');
      if (response.data.success) {
        setConfigurations(response.data.configurations);
        
        // Initialize editing state
        const editState = {};
        response.data.configurations.forEach(config => {
          editState[config.service_name] = {
            api_key: '',
            api_secret: '',
            additional_config: config.additional_config ? (typeof config.additional_config === 'string' ? JSON.parse(config.additional_config) : config.additional_config) : {},
            is_active: config.is_active,
            description: config.description || '',
          };
        });
        setEditing(editState);
      }
    } catch (error) {
      toast.error('Failed to load API configurations');
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = async () => {
    try {
      setInitializing(true);
      const response = await api.post('/admin/api-keys/initialize');
      if (response.data.success) {
        toast.success(response.data.message || 'Default configurations initialized');
        await fetchConfigurations();
      }
    } catch (error) {
      console.error('Initialize error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to initialize configurations';
      toast.error(errorMessage);
      
      // Show detailed error in development
      if (process.env.NODE_ENV === 'development' && error.response?.data?.details) {
        console.error('Error details:', error.response.data.details);
      }
    } finally {
      setInitializing(false);
    }
  };

  const handleChange = (serviceName, field, value) => {
    setEditing((prev) => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        [field]: value,
      },
    }));
  };

  const handleAdditionalConfigChange = (serviceName, key, value) => {
    setEditing((prev) => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        additional_config: {
          ...(prev[serviceName]?.additional_config || {}),
          [key]: value,
        },
      },
    }));
  };

  const handleSave = async (serviceName) => {
    try {
      setSaving((prev) => ({ ...prev, [serviceName]: true }));
      
      const config = editing[serviceName];
      const payload = {
        api_key: config.api_key || undefined,
        api_secret: config.api_secret || undefined,
        additional_config: config.additional_config || {},
        is_active: config.is_active,
        description: config.description || undefined,
      };

      const response = await api.put(`/admin/api-keys/${serviceName}`, payload);
      
      if (response.data.success) {
        toast.success(`${getConfig(serviceName).display_name} updated successfully`);
        fetchConfigurations();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  const toggleShowSecret = (serviceName) => {
    setShowSecrets((prev) => ({
      ...prev,
      [serviceName]: !prev[serviceName],
    }));
  };

  const getConfig = (serviceName) => {
    return configurations.find(c => c.service_name === serviceName) || {};
  };

  const getIcon = (serviceName) => {
    const IconComponent = serviceIcons[serviceName] || Key;
    return IconComponent;
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">API Keys & Integrations</h1>
            <p className="text-gray-600">Manage API keys and configurations for all integrations</p>
          </div>
          <div className="flex gap-3">
            {configurations.length === 0 && (
              <button
                onClick={initializeDefaults}
                disabled={initializing}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {initializing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2" />
                    Initialize Defaults
                  </>
                )}
              </button>
            )}
            <button
              onClick={fetchConfigurations}
              className="inline-flex items-center px-4 py-2 rounded-lg border border-purple-300 text-purple-700 font-semibold hover:bg-purple-50"
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {configurations.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-12 text-center">
            <Key className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No API Configurations Found</h2>
            <p className="text-gray-600 mb-6">
              Initialize default API configurations to get started
            </p>
            <button
              onClick={initializeDefaults}
              disabled={initializing}
              className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
            >
              {initializing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Initialize Default Configurations
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {configurations.map((config) => {
              const IconComponent = getIcon(config.service_name);
              const editData = editing[config.service_name] || {};
              const isSaving = saving[config.service_name];

              return (
                <div
                  key={config.id}
                  className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{config.display_name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {config.is_active ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {config.description && (
                    <p className="text-sm text-gray-600 mb-4">{config.description}</p>
                  )}

                  <div className="space-y-4">
                    {/* API Key */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets[config.service_name] ? 'text' : 'password'}
                          value={editData.api_key || ''}
                          onChange={(e) => handleChange(config.service_name, 'api_key', e.target.value)}
                          placeholder={
                            config.api_key_preview
                              ? `Current: ${config.api_key_preview}`
                              : 'Enter API key'
                          }
                          className="w-full px-4 py-2 pr-10 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret(config.service_name)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showSecrets[config.service_name] ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* API Secret (for services that need it) */}
                    {(config.service_name === 'stripe' || 
                      config.service_name === 'recaptcha') && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {config.service_name === 'stripe' ? 'Secret Key' : 'API Secret'}
                        </label>
                        <div className="relative">
                          <input
                            type={showSecrets[config.service_name + '_secret'] ? 'text' : 'password'}
                            value={editData.api_secret || ''}
                            onChange={(e) => handleChange(config.service_name, 'api_secret', e.target.value)}
                            placeholder={
                              config.api_secret_preview
                                ? `Current: ${config.api_secret_preview}`
                                : 'Enter API secret'
                            }
                            className="w-full px-4 py-2 pr-10 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => toggleShowSecret(config.service_name + '_secret')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showSecrets[config.service_name + '_secret'] ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Additional Config for Resend.com */}
                    {config.service_name === 'resend' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            From Name
                          </label>
                          <input
                            type="text"
                            value={editData.additional_config?.fromName || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'fromName', e.target.value)
                            }
                            placeholder="MyStoryMe"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">Display name for sender</p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            From Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={editData.additional_config?.fromEmail || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'fromEmail', e.target.value)
                            }
                            placeholder="noreply@yourdomain.com"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">Must be a verified domain in Resend.com</p>
                        </div>
                      </>
                    )}

                    {/* Additional Config for Stripe */}
                    {config.service_name === 'stripe' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Publishable Key
                        </label>
                        <input
                          type="text"
                          value={editData.additional_config?.publishable_key || ''}
                          onChange={(e) =>
                            handleAdditionalConfigChange(config.service_name, 'publishable_key', e.target.value)
                          }
                          placeholder="pk_live_... or pk_test_..."
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    )}

                    {/* Additional Config for reCAPTCHA */}
                    {config.service_name === 'recaptcha' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Site Key (for frontend)
                          </label>
                          <input
                            type="text"
                            value={editData.additional_config?.site_key || editData.api_key || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleAdditionalConfigChange(config.service_name, 'site_key', value);
                              // Also update api_key for backward compatibility
                              handleChange(config.service_name, 'api_key', value);
                            }}
                            placeholder="6LeIxAcT..."
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">Used in the frontend Contact form</p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Secret Key (for backend)
                          </label>
                          <div className="relative">
                            <input
                              type={showSecrets[config.service_name + '_secret'] ? 'text' : 'password'}
                              value={editData.api_secret || ''}
                              onChange={(e) => handleChange(config.service_name, 'api_secret', e.target.value)}
                              placeholder="Enter secret key"
                              className="w-full px-4 py-2 pr-10 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => toggleShowSecret(config.service_name + '_secret')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showSecrets[config.service_name + '_secret'] ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Used for server-side verification</p>
                        </div>
                      </>
                    )}

                    {/* Additional Config for Google Analytics */}
                    {config.service_name === 'google_analytics' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tracking ID
                        </label>
                        <input
                          type="text"
                          value={editData.api_key || ''}
                          onChange={(e) => handleChange(config.service_name, 'api_key', e.target.value)}
                          placeholder="G-XXXXXXXXXX or UA-XXXXX-X"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    )}

                    {/* Additional Config for Google Ads */}
                    {config.service_name === 'google_ads' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Conversion ID
                        </label>
                        <input
                          type="text"
                          value={editData.api_key || ''}
                          onChange={(e) => handleChange(config.service_name, 'api_key', e.target.value)}
                          placeholder="AW-XXXXXXXXX"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    )}

                    {/* Additional Config for SMTP */}
                    {config.service_name === 'smtp' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            SMTP Host
                          </label>
                          <input
                            type="text"
                            value={editData.additional_config?.host || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'host', e.target.value)
                            }
                            placeholder="smtp.gmail.com"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            SMTP Port
                          </label>
                          <input
                            type="number"
                            value={editData.additional_config?.port || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'port', e.target.value)
                            }
                            placeholder="587"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email/Username
                          </label>
                          <input
                            type="email"
                            value={editData.additional_config?.user || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'user', e.target.value)
                            }
                            placeholder="your-email@example.com"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Password/App Password
                          </label>
                          <div className="relative">
                            <input
                              type={showSecrets[config.service_name + '_password'] ? 'text' : 'password'}
                              value={editData.additional_config?.password || ''}
                              onChange={(e) =>
                                handleAdditionalConfigChange(config.service_name, 'password', e.target.value)
                              }
                              placeholder="Enter password or app password"
                              className="w-full px-4 py-2 pr-10 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => toggleShowSecret(config.service_name + '_password')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showSecrets[config.service_name + '_password'] ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            For Gmail, use an App Password. For other providers, use your email password.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            From Name
                          </label>
                          <input
                            type="text"
                            value={editData.additional_config?.fromName || ''}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'fromName', e.target.value)
                            }
                            placeholder="MyStoryMe"
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`secure-${config.service_name}`}
                            checked={editData.additional_config?.secure === true}
                            onChange={(e) =>
                              handleAdditionalConfigChange(config.service_name, 'secure', e.target.checked)
                            }
                            className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <label
                            htmlFor={`secure-${config.service_name}`}
                            className="text-sm font-medium text-gray-700"
                          >
                            Use secure connection (TLS/SSL)
                          </label>
                        </div>
                      </>
                    )}

                    {/* Active Toggle */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id={`active-${config.service_name}`}
                        checked={editData.is_active || false}
                        onChange={(e) => handleChange(config.service_name, 'is_active', e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label
                        htmlFor={`active-${config.service_name}`}
                        className="text-sm font-medium text-gray-700"
                      >
                        Enable this integration
                      </label>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={() => handleSave(config.service_name)}
                      disabled={isSaving}
                      className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Configuration
                        </>
                      )}
                    </button>
                  </div>

                  {config.updated_at && (
                    <p className="text-xs text-gray-500 mt-4">
                      Last updated: {new Date(config.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Security Note</h4>
              <p className="text-sm text-blue-700 mb-2">
                API keys and secrets are encrypted and stored securely. Only authorized administrators can view and modify these configurations.
              </p>
              <p className="text-sm text-blue-700">
                These configurations are used dynamically throughout the application. Changes take effect immediately after saving.
              </p>
            </div>
          </div>
        </div>
    </AdminSidebar>
  );
};

export default AdminApiKeys;

